import React, { useState, useEffect } from "react";
import {
  Palette, HelpCircle, Save, X, AlertCircle, Plus, Trash2, Edit, ExternalLink
} from "lucide-react";
import PageHeader from "../../components/PageHeader";
import StatusBadge from "../../components/StatusBadge";
import {
  listGlobalSettings, updateGlobalSetting, bulkUpdateSettings,
  listHelps, createHelp, updateHelp, deleteHelp
} from "../../services/adminSettings";

const WHITELABEL_KEYS = [
  { key: "primaryColorLight", label: "Cor Primária (Light)", type: "color" },
  { key: "primaryColorDark", label: "Cor Secundária (Dark)", type: "color" },
  { key: "secondaryColorLight", label: "Cor Secundária (Light)", type: "color" },
  { key: "secondaryColorDark", label: "Cor Secundária (Dark)", type: "color" },
  { key: "useGradient", label: "Usar Gradiente", type: "boolean" },
  { key: "appName", label: "Nome do Aplicativo", type: "text" },
  { key: "enabledLanguages", label: "Idiomas Ativos", type: "text" }
];

const HOMEPAGE_KEYS = [
  { key: "homePagePrimaryColor", label: "Cor Primária", type: "color" },
  { key: "homePageBackgroundColor", label: "Cor de Fundo", type: "color" },
  { key: "homePageTextColor", label: "Cor do Texto", type: "color" },
  { key: "homePageAccentColor", label: "Cor de Destaque", type: "color" },
  { key: "homePageFontFamily", label: "Fonte", type: "text" },
  { key: "homePageFontSize", label: "Tamanho da Fonte (px)", type: "text" },
  { key: "homePageHeroTitle", label: "Título Hero", type: "text" },
  { key: "homePageHeroDescription", label: "Descrição Hero", type: "text" },
  { key: "homePageHeroBadge", label: "Badge Hero", type: "text" },
  { key: "homePageFeaturesTitle", label: "Título Recursos", type: "text" },
  { key: "homePageFeaturesDescription", label: "Descrição Recursos", type: "text" },
  { key: "homePagePricingTitle", label: "Título Preços", type: "text" },
  { key: "homePagePricingDescription", label: "Descrição Preços", type: "text" },
  { key: "homePageCtaTitle", label: "Título CTA", type: "text" },
  { key: "homePageCtaDescription", label: "Descrição CTA", type: "text" }
];

const LOGO_KEYS = [
  { key: "appLogoLight", label: "Logo Light" },
  { key: "appLogoDark", label: "Logo Dark" },
  { key: "appLogoFavicon", label: "Favicon" },
  { key: "appLogoBackgroundLight", label: "Background Light" },
  { key: "appLogoBackgroundDark", label: "Background Dark" }
];

const GlobalSettings = () => {
  const [activeTab, setActiveTab] = useState("whitelabel");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [toast, setToast] = useState(null);
  const [settings, setSettings] = useState({});
  const [helps, setHelps] = useState([]);
  const [helpModal, setHelpModal] = useState({ open: false, mode: "create", item: null });

  useEffect(() => { loadSettings(); loadHelps(); }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const data = await listGlobalSettings({ pageSize: 100 });
      const map = {};
      (data.items || []).forEach(s => { map[s.key] = s.value; });
      setSettings(map);
    } catch (err) { setError(err.message || "Erro ao carregar configurações"); }
    finally { setLoading(false); }
  };

  const loadHelps = async () => {
    try {
      const data = await listHelps({ pageSize: 100 });
      setHelps(data.records || []);
    } catch { /* optional */ }
  };

  const handleColorChange = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const handleTextChange = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const handleBooleanToggle = (key) => {
    setSettings(prev => ({ ...prev, [key]: prev[key] === "true" ? "false" : "true" }));
  };

  const handleSaveWhitelabel = async () => {
    try {
      const updates = WHITELABEL_KEYS.map(k => ({ key: k.key, value: settings[k.key] || "" }));
      await bulkUpdateSettings(updates);
      setToast({ type: "success", message: "Whitelabel atualizado" });
    } catch (err) { setToast({ type: "error", message: err.message || "Erro ao salvar" }); }
  };

  const handleSaveHelp = async () => {
    const { mode, item } = helpModal;
    if (!item?.title?.trim()) { setToast({ type: "error", message: "Título é obrigatório" }); return; }
    try {
      if (mode === "create") {
        await createHelp({ title: item.title, description: item.description || "", video: item.video || "", link: item.link || "" });
        setToast({ type: "success", message: "Item criado" });
      } else {
        await updateHelp(item.id, { title: item.title, description: item.description || "", video: item.video || "", link: item.link || "" });
        setToast({ type: "success", message: "Item atualizado" });
      }
      setHelpModal({ open: false, mode: "create", item: null });
      loadHelps();
    } catch (err) { setToast({ type: "error", message: err.message || "Erro ao salvar" }); }
  };

  const handleDeleteHelp = async (id) => {
    try {
      await deleteHelp(id);
      setToast({ type: "success", message: "Item excluído" });
      loadHelps();
    } catch (err) { setToast({ type: "error", message: err.message || "Erro ao excluir" }); }
  };

  return (
    <div className="space-y-4">
      <PageHeader
        breadcrumb="Configurações"
        title="Configurações Globais"
        subtitle="Branding, tema e ajuda da plataforma"
      />

      {error && (
        <div className="p-3 rounded-xl bg-rose-500/10 border border-rose-500/20 text-rose-600 dark:text-rose-300 text-xs font-semibold flex items-center gap-3">
          <AlertCircle size={16} /> {error}
        </div>
      )}

      {toast && (
        <div className={`p-3 rounded-xl border text-xs font-semibold flex items-center justify-between ${
          toast.type === "success" ? "bg-emerald-500/10 border-emerald-500/20 text-emerald-700 dark:text-emerald-300" : "bg-rose-500/10 border-rose-500/20 text-rose-700 dark:text-rose-300"
        }`}>
          <span>{toast.message}</span>
          <button onClick={() => setToast(null)} className="text-gray-400 hover:text-gray-700 dark:hover:text-white"><X size={14} /></button>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-200 dark:border-white/10 pb-0">
        <button onClick={() => setActiveTab("whitelabel")} className={`px-4 py-2 text-xs font-semibold rounded-t-xl transition-colors ${activeTab === "whitelabel" ? "bg-purple-600 text-white" : "bg-gray-100 dark:bg-white/5 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-white/10"}`}>
          <Palette size={14} className="inline mr-1" /> Whitelabel
        </button>
        <button onClick={() => setActiveTab("homepage")} className={`px-4 py-2 text-xs font-semibold rounded-t-xl transition-colors ${activeTab === "homepage" ? "bg-purple-600 text-white" : "bg-gray-100 dark:bg-white/5 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-white/10"}`}>
          <Palette size={14} className="inline mr-1" /> Homepage
        </button>
        <button onClick={() => setActiveTab("helps")} className={`px-4 py-2 text-xs font-semibold rounded-t-xl transition-colors ${activeTab === "helps" ? "bg-purple-600 text-white" : "bg-gray-100 dark:bg-white/5 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-white/10"}`}>
          <HelpCircle size={14} className="inline mr-1" /> Ajuda
        </button>
      </div>

      {/* Whitelabel Tab */}
      {activeTab === "whitelabel" && (
        <div className="space-y-4">
          <div className="neumorphic-card rounded-2xl p-5 border border-gray-200 dark:border-white/10 space-y-4">
            <h3 className="text-sm font-bold text-gray-900 dark:text-white">Cores do Tema</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {WHITELABEL_KEYS.filter(k => k.type === "color").map(item => (
                <div key={item.key} className="space-y-1">
                  <label className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase">{item.label}</label>
                  <div className="flex items-center gap-2">
                    <input type="color" value={settings[item.key] || "#8b5cf6"} onChange={(e) => handleColorChange(item.key, e.target.value)}
                      className="w-8 h-8 rounded-lg border border-gray-200 dark:border-white/10 cursor-pointer" />
                    <input type="text" value={settings[item.key] || ""} onChange={(e) => handleColorChange(item.key, e.target.value)}
                      className="flex-1 px-2 py-1 bg-gray-50 dark:bg-[#191c1e] border border-gray-200 dark:border-white/10 rounded-lg text-[10px] font-mono text-gray-900 dark:text-white" />
                  </div>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <label className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase">Usar Gradiente</label>
                <button onClick={() => handleBooleanToggle("useGradient")}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${settings.useGradient === "true" ? "bg-emerald-500 text-white" : "bg-gray-200 dark:bg-white/10 text-gray-600 dark:text-gray-400"}`}>
                  {settings.useGradient === "true" ? "Ativado" : "Desativado"}
                </button>
              </div>
              <div className="space-y-1">
                <label className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase">Nome do App</label>
                <input type="text" value={settings.appName || ""} onChange={(e) => handleTextChange("appName", e.target.value)}
                  className="w-full px-3 py-1.5 bg-gray-50 dark:bg-[#191c1e] border border-gray-200 dark:border-white/10 rounded-lg text-xs text-gray-900 dark:text-white" />
              </div>
            </div>

            <div className="space-y-1">
              <label className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase">Idiomas Ativos</label>
              <input type="text" value={settings.enabledLanguages || ""} onChange={(e) => handleTextChange("enabledLanguages", e.target.value)}
                placeholder="ex: pt,en,es" className="w-full px-3 py-1.5 bg-gray-50 dark:bg-[#191c1e] border border-gray-200 dark:border-white/10 rounded-lg text-xs text-gray-900 dark:text-white" />
            </div>

            <div className="flex justify-end">
              <button onClick={handleSaveWhitelabel} className="flex items-center gap-1.5 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-xs font-bold text-white rounded-xl">
                <Save size={12} /> Salvar Whitelabel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Homepage Tab */}
      {activeTab === "homepage" && (
        <div className="space-y-4">
          <div className="neumorphic-card rounded-2xl p-5 border border-gray-200 dark:border-white/10 space-y-4">
            <h3 className="text-sm font-bold text-gray-900 dark:text-white">Personalização da Landing Page</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">Essas configurações são aplicadas globalmente na página pública.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {HOMEPAGE_KEYS.filter(k => k.type === "color").map(item => (
                <div key={item.key} className="space-y-1">
                  <label className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase">{item.label}</label>
                  <div className="flex items-center gap-2">
                    <input type="color" value={settings[item.key] || "#8b5cf6"} onChange={(e) => handleColorChange(item.key, e.target.value)}
                      className="w-8 h-8 rounded-lg border border-gray-200 dark:border-white/10 cursor-pointer" />
                    <input type="text" value={settings[item.key] || ""} onChange={(e) => handleColorChange(item.key, e.target.value)}
                      className="flex-1 px-2 py-1 bg-gray-50 dark:bg-[#191c1e] border border-gray-200 dark:border-white/10 rounded-lg text-[10px] font-mono text-gray-900 dark:text-white" />
                  </div>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {HOMEPAGE_KEYS.filter(k => k.type === "text").map(item => (
                <div key={item.key} className="space-y-1">
                  <label className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase">{item.label}</label>
                  <input type="text" value={settings[item.key] || ""} onChange={(e) => handleTextChange(item.key, e.target.value)}
                    className="w-full px-3 py-1.5 bg-gray-50 dark:bg-[#191c1e] border border-gray-200 dark:border-white/10 rounded-lg text-xs text-gray-900 dark:text-white" />
                </div>
              ))}
            </div>
            <div className="flex justify-end">
              <button onClick={async () => {
                try {
                  const updates = HOMEPAGE_KEYS.map(k => ({ key: k.key, value: settings[k.key] || "" }));
                  await bulkUpdateSettings(updates);
                  setToast({ type: "success", message: "Homepage atualizada" });
                } catch (err) { setToast({ type: "error", message: err.message || "Erro ao salvar" }); }
              }} className="flex items-center gap-1.5 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-xs font-bold text-white rounded-xl">
                <Save size={12} /> Salvar Homepage
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Helps Tab */}
      {activeTab === "helps" && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <span className="text-xs text-gray-500">{helps.length} item(ns) de ajuda</span>
            <button onClick={() => setHelpModal({ open: true, mode: "create", item: { title: "", description: "", video: "", link: "" } })}
              className="flex items-center gap-1.5 px-4 py-2 create-event-btn text-white text-xs font-bold rounded-xl shadow-purple-glow">
              <Plus size={14} /> Novo Item
            </button>
          </div>

          {helps.map(help => (
            <div key={help.id} className="neumorphic-card rounded-xl p-4 border border-gray-200 dark:border-white/10 flex items-center justify-between">
              <div className="flex-1 min-w-0">
                <p className="text-xs font-bold text-gray-900 dark:text-white truncate">{help.title}</p>
                {help.description && <p className="text-[10px] text-gray-500 dark:text-gray-400 truncate mt-0.5">{help.description}</p>}
                <div className="flex gap-2 mt-1">
                  {help.video && <span className="text-[10px] text-purple-500">Video</span>}
                  {help.link && <a href={help.link} target="_blank" rel="noopener noreferrer" className="text-[10px] text-blue-500 hover:underline flex items-center gap-0.5"><ExternalLink size={8} /> Link</a>}
                </div>
              </div>
              <div className="flex items-center gap-1.5 ml-3">
                <button onClick={() => setHelpModal({ open: true, mode: "edit", item: help })}
                  className="p-1 rounded-lg bg-black/5 dark:bg-white/5 hover:bg-purple-500/20 text-purple-600 dark:text-purple-400 transition-colors" title="Editar">
                  <Edit size={14} />
                </button>
                <button onClick={() => handleDeleteHelp(help.id)}
                  className="p-1 rounded-lg bg-black/5 dark:bg-white/5 hover:bg-rose-500/20 text-rose-600 dark:text-rose-400 transition-colors" title="Excluir">
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}

          {helps.length === 0 && !loading && (
            <div className="text-center py-8 text-xs text-gray-400">Nenhum item de ajuda encontrado</div>
          )}
        </div>
      )}

      {/* Help Modal */}
      {helpModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="neumorphic-card rounded-3xl w-full max-w-md p-6 border border-gray-200 dark:border-white/10 relative space-y-4">
            <div className="flex items-center justify-between border-b border-gray-100 dark:border-white/5 pb-3">
              <h3 className="text-base font-bold text-gray-900 dark:text-white">{helpModal.mode === "create" ? "Novo Item" : "Editar Item"}</h3>
              <button onClick={() => setHelpModal({ open: false, mode: "create", item: null })} className="text-gray-400 hover:text-gray-700 dark:hover:text-white"><X size={18} /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1 block">Título *</label>
                <input type="text" value={helpModal.item?.title || ""} onChange={(e) => setHelpModal(prev => ({ ...prev, item: { ...prev.item, title: e.target.value } }))}
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-[#191c1e] border border-gray-200 dark:border-white/10 rounded-xl text-xs text-gray-900 dark:text-white focus:outline-none focus:border-purple-500" />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1 block">Descrição</label>
                <textarea value={helpModal.item?.description || ""} onChange={(e) => setHelpModal(prev => ({ ...prev, item: { ...prev.item, description: e.target.value } }))}
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-[#191c1e] border border-gray-200 dark:border-white/10 rounded-xl text-xs text-gray-900 dark:text-white focus:outline-none focus:border-purple-500" rows={3} />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1 block">URL do Vídeo</label>
                <input type="text" value={helpModal.item?.video || ""} onChange={(e) => setHelpModal(prev => ({ ...prev, item: { ...prev.item, video: e.target.value } }))}
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-[#191c1e] border border-gray-200 dark:border-white/10 rounded-xl text-xs text-gray-900 dark:text-white focus:outline-none focus:border-purple-500" />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1 block">Link Externo</label>
                <input type="text" value={helpModal.item?.link || ""} onChange={(e) => setHelpModal(prev => ({ ...prev, item: { ...prev.item, link: e.target.value } }))}
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-[#191c1e] border border-gray-200 dark:border-white/10 rounded-xl text-xs text-gray-900 dark:text-white focus:outline-none focus:border-purple-500" />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-3 border-t border-gray-100 dark:border-white/5">
              <button onClick={() => setHelpModal({ open: false, mode: "create", item: null })} className="px-4 py-2 bg-gray-100 dark:bg-white/5 hover:bg-gray-200 dark:hover:bg-white/10 text-xs font-semibold text-gray-700 dark:text-gray-300 rounded-xl">Cancelar</button>
              <button onClick={handleSaveHelp} className="px-5 py-2 bg-purple-600 hover:bg-purple-700 text-xs font-bold text-white rounded-xl">
                {helpModal.mode === "create" ? "Criar" : "Salvar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GlobalSettings;
